import { useMemo, useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { comboLabel, useRefData, type Combination } from '../../lib/refdata';
import type { Supplementary, Virement, VirementRule } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const TIER_ROLE: Record<Virement['tier'], string> = {
  MDA_INTERNAL: 'MDA_PS',
  MOF: 'PS_FINANCE',
  CABINET: 'CABINET_SEC',
  ASSEMBLY: 'CABINET_SEC',
};

export function Virements() {
  const { persona } = useAuth();
  const { fy } = useRefData();
  const [showRequest, setShowRequest] = useState(false);
  const [showRule, setShowRule] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<Virement | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const registerQ = useApiQuery<{ items: Virement[] }>(fy ? `/api/v1/virements/register/${fy.id}` : null);
  const rulesQ = useApiQuery<{ items: VirementRule[] }>('/api/v1/virements/rules');
  const combosQ = useApiQuery<{ items: Combination[] }>('/api/v1/coa/combinations?limit=100');
  const comboById = useMemo(() => new Map((combosQ.data?.items ?? []).map((c) => [c.id, c])), [combosQ.data]);

  const canRequest =
    (persona?.roles.includes('MDA_FINANCE_OFFICER') ||
      persona?.roles.includes('MDA_PS') ||
      persona?.roles.includes('MOF_BUDGET_ANALYST')) ??
    false;
  const canOwnRules = persona?.roles.includes('PS_FINANCE') ?? false;
  const canSubmitSupplementary = persona?.roles.includes('MOF_BUDGET_ANALYST') ?? false;

  const [supplementaries, setSupplementaries] = useState<Supplementary[]>([]);
  const [showSupplementary, setShowSupplementary] = useState(false);

  const act = async (v: Virement, action: 'approve' | 'reject', reason?: string) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/virements/${v.id}/${action}`, action === 'reject' ? { reason } : undefined);
      registerQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    }
  };

  const patchSupplementary = (id: string, patch: Partial<Supplementary>) =>
    setSupplementaries((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  if (!fy) return <ErrorNote message="No OPEN fiscal year." />;

  return (
    <div>
      <PageHeader
        title="Virements & Supplementary Estimates"
        subtitle="In-year legal budget changes — the rule table assigns the sign-off tier automatically from the economic classes moved"
        actions={
          canRequest ? (
            <button className="btn-primary" onClick={() => setShowRequest(true)}>
              + Request virement
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">Public register — Fiscal Year {fy.year}</h2>
        </div>
        <div className="card overflow-x-auto">
          {registerQ.loading ? (
            <Spinner />
          ) : registerQ.error ? (
            <ErrorNote message={registerQ.error} />
          ) : (registerQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No virements this fiscal year" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Ref</th>
                  <th className="table-head">From → To</th>
                  <th className="table-head text-right">Amount</th>
                  <th className="table-head">Tier</th>
                  <th className="table-head">Status</th>
                  <th className="table-head" />
                </tr>
              </thead>
              <tbody>
                {registerQ.data!.items.map((v) => {
                  return (
                    <tr key={v.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{v.refNumber}</td>
                      <td className="table-cell text-xs">
                        <div title={comboById.get(v.fromCoaId) ? comboLabel(comboById.get(v.fromCoaId)!) : v.fromCoaId}>{v.fromClass}</div>
                        <div className="text-slate-400">→ {v.toClass}</div>
                      </td>
                      <td className="table-cell text-right tabular-nums">{scr(v.amount)}</td>
                      <td className="table-cell">
                        <StatusBadge status={v.tier} />
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={v.status} />
                        {v.status === 'REJECTED' && v.rejectReason ? <div className="mt-1 text-[11px] text-rose-600">{v.rejectReason}</div> : null}
                      </td>
                      <td className="table-cell text-right">
                        {v.status === 'REQUESTED' && persona?.roles.includes(TIER_ROLE[v.tier]) ? (
                          <div className="flex justify-end gap-1.5">
                            <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act(v, 'approve')}>
                              Approve
                            </button>
                            <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setRejectTarget(v)}>
                              Reject
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Virement rule table</h2>
            {canOwnRules ? (
              <button className="btn-secondary !py-1 text-xs" onClick={() => setShowRule(true)}>
                + Add rule
              </button>
            ) : null}
          </div>
          <div className="card overflow-x-auto">
            {rulesQ.loading ? (
              <Spinner />
            ) : rulesQ.error ? (
              <ErrorNote message={rulesQ.error} />
            ) : (rulesQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No rules — every virement defaults to the MOF tier" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">From → To class</th>
                    <th className="table-head">Cap</th>
                    <th className="table-head">Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {rulesQ.data!.items.map((r) => (
                    <tr key={r.id}>
                      <td className="table-cell text-xs">
                        {r.fromClass} → {r.toClass}
                      </td>
                      <td className="table-cell text-xs">{r.maxPercent ? `${r.maxPercent}% of original` : '—'}</td>
                      <td className="table-cell">
                        <StatusBadge status={r.tierRequired} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Supplementary estimates (this session)</h2>
            {canSubmitSupplementary ? (
              <button className="btn-secondary !py-1 text-xs" onClick={() => setShowSupplementary(true)}>
                + Draft supplementary
              </button>
            ) : null}
          </div>
          <div className="card overflow-x-auto">
            {supplementaries.length === 0 ? (
              <EmptyState
                title="No supplementaries drafted yet"
                hint="There is no list endpoint for supplementaries — this app shows only what you draft in this browser session."
              />
            ) : (
              supplementaries.map((s) => (
                <div key={s.id} className="flex items-center justify-between border-b border-slate-100 px-4 py-3 last:border-b-0">
                  <div>
                    <div className="text-sm font-semibold">{s.billRef}</div>
                    <div className="text-xs text-slate-500">
                      {s.lines.length} line(s) · {scr(s.lines.reduce((sum, l) => sum + l.amount, 0))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={s.status} />
                    {canSubmitSupplementary && s.status === 'DRAFT' ? (
                      <button
                        className="btn-secondary !px-2.5 !py-1 text-xs"
                        onClick={async () => {
                          await api.post(`/api/v1/virements/supplementaries/${s.id}/table`);
                          patchSupplementary(s.id, { status: 'TABLED' });
                        }}
                      >
                        Table in Assembly
                      </button>
                    ) : null}
                    {canSubmitSupplementary && s.status === 'TABLED' ? (
                      <button
                        className="btn-primary !px-2.5 !py-1 text-xs"
                        onClick={async () => {
                          await api.post(`/api/v1/virements/supplementaries/${s.id}/enact`);
                          patchSupplementary(s.id, { status: 'ENACTED' });
                        }}
                      >
                        Enact → top up F03
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showRequest && combosQ.data ? (
        <RequestModal
          fyId={fy.id}
          combos={combosQ.data.items}
          onClose={() => setShowRequest(false)}
          onDone={() => {
            setShowRequest(false);
            registerQ.reload();
          }}
        />
      ) : null}
      {showRule ? (
        <RuleModal
          onClose={() => setShowRule(false)}
          onDone={() => {
            setShowRule(false);
            rulesQ.reload();
          }}
        />
      ) : null}
      {showSupplementary ? (
        <SupplementaryModal
          fyId={fy.id}
          combos={combosQ.data?.items ?? []}
          onClose={() => setShowSupplementary(false)}
          onDone={(s) => {
            setShowSupplementary(false);
            setSupplementaries((prev) => [s, ...prev]);
          }}
        />
      ) : null}
      {rejectTarget ? (
        <RejectModal
          virement={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onDone={(reason) => {
            setRejectTarget(null);
            act(rejectTarget, 'reject', reason);
          }}
        />
      ) : null}
    </div>
  );
}

function RequestModal({
  fyId,
  combos,
  onClose,
  onDone,
}: {
  fyId: string;
  combos: Combination[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [fromCoaId, setFromCoaId] = useState('');
  const [toCoaId, setToCoaId] = useState('');
  const [amount, setAmount] = useState('');
  const [justification, setJustification] = useState('');
  const [preview, setPreview] = useState<{ tier: string; refNumber: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<Virement>('/api/v1/virements/', {
        fiscalYearId: fyId,
        fromCoaId,
        toCoaId,
        amount: Number.parseFloat(amount),
        justification,
      });
      setPreview({ tier: result.tier, refNumber: result.refNumber });
      setTimeout(onDone, 900);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to request virement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Request virement" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {preview ? (
          <div className="rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">
            {preview.refNumber} requested — assigned to the <strong>{preview.tier}</strong> approval tier.
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="From budget line">
            <select className="field-input" value={fromCoaId} onChange={(e) => setFromCoaId(e.target.value)}>
              <option value="">Select…</option>
              {combos.map((c) => (
                <option key={c.id} value={c.id}>
                  {comboLabel(c)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="To budget line">
            <select className="field-input" value={toCoaId} onChange={(e) => setToCoaId(e.target.value)}>
              <option value="">Select…</option>
              {combos.map((c) => (
                <option key={c.id} value={c.id}>
                  {comboLabel(c)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Amount (SCR)">
          <input className="field-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Justification">
          <textarea className="field-input" rows={3} value={justification} onChange={(e) => setJustification(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !fromCoaId || !toCoaId || fromCoaId === toCoaId || !amount || justification.length < 10}
          >
            {busy ? 'Requesting…' : 'Request virement'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RuleModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [fromClass, setFromClass] = useState('');
  const [toClass, setToClass] = useState('');
  const [maxPercent, setMaxPercent] = useState('');
  const [tierRequired, setTierRequired] = useState<VirementRule['tierRequired']>('MDA_INTERNAL');
  const [priority, setPriority] = useState('10');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/virements/rules', {
        fromClass,
        toClass,
        tierRequired,
        priority: Number.parseInt(priority, 10),
        ...(maxPercent ? { maxPercent: Number.parseFloat(maxPercent) } : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create rule');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add virement rule" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="From class (or *)">
            <input className="field-input" value={fromClass} onChange={(e) => setFromClass(e.target.value)} placeholder="e.g. GOODS_SERVICES" />
          </Field>
          <Field label="To class (or *)">
            <input className="field-input" value={toClass} onChange={(e) => setToClass(e.target.value)} placeholder="e.g. WAGES" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Tier required">
            <select className="field-input" value={tierRequired} onChange={(e) => setTierRequired(e.target.value as VirementRule['tierRequired'])}>
              <option value="MDA_INTERNAL">MDA_INTERNAL</option>
              <option value="MOF">MOF</option>
              <option value="CABINET">CABINET</option>
              <option value="ASSEMBLY">ASSEMBLY</option>
            </select>
          </Field>
          <Field label="Max % of original (optional)">
            <input className="field-input" type="number" min="0" max="100" value={maxPercent} onChange={(e) => setMaxPercent(e.target.value)} />
          </Field>
          <Field label="Priority (lowest wins)">
            <input className="field-input" type="number" min="1" value={priority} onChange={(e) => setPriority(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !fromClass || !toClass}>
            {busy ? 'Saving…' : 'Add rule'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SupplementaryModal({
  fyId,
  combos,
  onClose,
  onDone,
}: {
  fyId: string;
  combos: Combination[];
  onClose: () => void;
  onDone: (s: Supplementary) => void;
}) {
  const [billRef, setBillRef] = useState('');
  const [lines, setLines] = useState<Array<{ coaCombinationId: string; amount: string }>>([{ coaCombinationId: '', amount: '' }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const supplementary = await api.post<Supplementary>('/api/v1/virements/supplementaries', {
        fiscalYearId: fyId,
        billRef,
        lines: lines
          .filter((l) => l.coaCombinationId && l.amount)
          .map((l) => ({ coaCombinationId: l.coaCombinationId, amount: Number.parseFloat(l.amount) })),
      });
      onDone(supplementary);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to draft supplementary');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Draft supplementary estimate" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Bill reference">
          <input className="field-input" value={billRef} onChange={(e) => setBillRef(e.target.value)} placeholder="e.g. SUPP-2026-02" />
        </Field>
        {lines.map((row, idx) => (
          <div key={idx} className="flex gap-2">
            <select
              className="field-input flex-1"
              value={row.coaCombinationId}
              onChange={(e) => setLines(lines.map((r, i) => (i === idx ? { ...r, coaCombinationId: e.target.value } : r)))}
            >
              <option value="">Select budget line…</option>
              {combos.map((c) => (
                <option key={c.id} value={c.id}>
                  {comboLabel(c)}
                </option>
              ))}
            </select>
            <input
              className="field-input w-40"
              type="number"
              placeholder="Amount"
              value={row.amount}
              onChange={(e) => setLines(lines.map((r, i) => (i === idx ? { ...r, amount: e.target.value } : r)))}
            />
            <button className="btn-secondary !px-2.5" onClick={() => setLines(lines.filter((_, i) => i !== idx))}>
              ✕
            </button>
          </div>
        ))}
        <button className="btn-secondary !py-1.5 text-xs" onClick={() => setLines([...lines, { coaCombinationId: '', amount: '' }])}>
          + Add line
        </button>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !billRef || lines.every((l) => !l.coaCombinationId)}>
            {busy ? 'Drafting…' : 'Draft supplementary'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RejectModal({ virement, onClose, onDone }: { virement: Virement; onClose: () => void; onDone: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal title={`Reject ${virement.refNumber}`} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Reason">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={() => onDone(reason)} disabled={reason.length < 3}>
            Reject virement
          </button>
        </div>
      </div>
    </Modal>
  );
}
